export interface Chapter {
  id: number
  title: string
  objective: string
  example: string
  framework: string
  content: string[] // Array of paragraphs for the main body
}

export interface Book {
  title: string
  subtitle: string
  author: string
  disclaimer: string
  coverImage: string
  chapters: Chapter[]
}

export const bookContent: Book = {
  title: 'Rutinas Doradas',
  subtitle: 'Bienestar Diario para Mujeres 65+',
  author: 'Ana Sofía López',
  disclaimer:
    'Este material es de carácter informativo y no constituye asesoramiento médico o profesional. Consulta siempre a tu especialista antes de realizar cambios en tu rutina.',
  coverImage:
    'https://cdn.magicpatterns.com/uploads/uisbcPbYNBiXNRFEXBwWAT/PORTADA_DEL_LIBRO.png',
  chapters: [
    {
      id: 1,
      title: 'Introducción al Bienestar Diario',
      objective:
        'Explicar por qué pequeñas acciones diarias crean grandes cambios.',
      example:
        'María, 70 años, recuperó su energía incorporando solo 2 micro-hábitos diarios.',
      framework: '“La Fórmula 1%”: mejorar un poco cada día.',
      content: [
        'Bienvenida a un nuevo capítulo de tu vida. A menudo pensamos que para sentirnos mejor necesitamos cambios drásticos, pero la realidad es mucho más amable.',
        'El bienestar diario no se trata de grandes esfuerzos, sino de pequeñas pausas y momentos que te regalas a ti misma. Es la suma de estos pequeños gestos lo que construye una vida plena y llena de energía.',
        "En este libro, exploraremos cómo integrar 'micro-hábitos' que no requieren fuerza de voluntad, sino simplemente un poco de intención y cariño hacia una misma.",
      ],
    },
    {
      id: 2,
      title: 'Identificando Tu Ritmo Personal',
      objective: 'Ayudar a la lectora a entender su nivel actual de energía.',
      example:
        'Cuestionario simple para identificar horarios óptimos (mañana, tarde, noche).',
      framework:
        'Tabla simple para observar patrones de energía 3 días seguidos.',
      content: [
        'Cada una de nosotras tiene un reloj interno único. Algunas somos alondras mañaneras, llenas de energía con el primer rayo de sol. Otras, encontramos nuestra calma y fuerza por la tarde.',
        "No existe un ritmo 'correcto'. Lo importante es conocer el tuyo. Forzarnos a actividades intensas cuando nuestro cuerpo pide descanso solo genera frustración.",
        'Te invito a observar tu energía durante los próximos tres días. ¿En qué momento te sientes más lúcida? ¿Cuándo te pide el cuerpo un descanso? Escuchar estas señales es el primer paso para una rutina dorada.',
      ],
    },
    {
      id: 3,
      title: 'Primer Micro-hábito: Mañanas con Intención',
      objective: 'Introducir un micro-hábito matutino.',
      example:
        'Beber un vaso de agua con limón y 2 minutos de respiración calmada.',
      framework: 'Checklist: 5 días de seguimiento visual.',
      content: [
        'La forma en que empezamos el día a menudo dicta cómo nos sentiremos el resto de la jornada. No necesitas una rutina de una hora. Bastan cinco minutos.',
        'Te propongo un comienzo suave: hidrátate antes de cualquier otra cosa. Un vaso de agua, quizás con unas gotas de limón, despierta tu sistema digestivo con suavidad.',
        'Acompáñalo de dos minutos de silencio o respiración consciente antes de encender la televisión o mirar el teléfono. Regálate ese primer momento del día exclusivamente para ti.',
      ],
    },
    {
      id: 4,
      title: 'Micro-hábitos de Movimiento',
      objective: 'Incorporar movimiento suave sin esfuerzo excesivo.',
      example: 'Estiramiento de brazos en silla durante 3 minutos.',
      framework: 'Escala: 1 (muy fácil) a 5 (moderado).',
      content: [
        "El movimiento es vida, pero no necesita ser extenuante para ser efectivo. A veces, la palabra 'ejercicio' nos asusta o nos da pereza.",
        "Cambiemos 'ejercicio' por 'movimiento consciente'. Puede ser tan simple como estirar los brazos hacia el cielo mientras estás sentada en tu sillón favorito, o hacer círculos suaves con los tobillos antes de levantarte de la cama.",
        'El objetivo es lubricar las articulaciones y despertar los músculos con amabilidad, sin dolor y sin fatiga excesiva.',
      ],
    },
    {
      id: 5,
      title: 'Nutrición y Micro-hábitos de Energía',
      objective: 'Añadir un micro-hábito alimenticio.',
      example: 'Fruta fresca o nueces a media mañana.',
      framework:
        'Checklist: Tabla para anotar nivel de energía antes y después del micro-hábito.',
      content: [
        'La comida es la gasolina de nuestro cuerpo. A medida que maduramos, nuestra digestión cambia y a veces necesitamos porciones más pequeñas pero más nutritivas.',
        'Un pequeño cambio puede ser introducir un snack de media mañana que te aporte energía real, no un pico de azúcar momentáneo. Un puñado de nueces o una pieza de fruta fresca son opciones maravillosas.',
        'Observa cómo te sientes media hora después de comerlo. ¿Te sientes más ligera? ¿Con más claridad mental? Tu cuerpo te dará la respuesta.',
      ],
    },
    {
      id: 6,
      title: 'Micro-hábitos de Relajación Nocturna',
      objective: 'Mejorar el descanso.',
      example: 'Respiración 4-4-6 antes de dormir.',
      framework:
        'Diario de sueño: anotar qué micro-hábito se hizo y cómo fue la noche.',
      content: [
        'El descanso reparador es el pilar de la salud. Preparar el cuerpo para dormir es tan importante como el sueño mismo.',
        'Intenta desconectar de las pantallas una hora antes de ir a la cama. La luz azul confunde a nuestro cerebro y retrasa la producción de melatonina.',
        'Prueba la respiración 4-4-6 ya en la cama: inhala en 4 tiempos, retén el aire 4 tiempos y exhala suavemente en 6 tiempos. Esto le indica a tu sistema nervioso que es hora de bajar las revoluciones y descansar.',
      ],
    },
    {
      id: 7,
      title: 'Creando Tu Rutina Personalizada',
      objective: 'Combinar los micro-hábitos en una rutina diaria.',
      example:
        'Modelo de rutina: mañana (micro-hábito 1), mediodía (micro-hábito 2), noche (micro-hábito 3).',
      framework:
        'Plantilla para diseñar su propia rutina de mañana/tarde/noche.',
      content: [
        'Ahora que has probado diferentes micro-hábitos, es momento de tejerlos en tu día a día. No intentes hacer todo a la vez.',
        'Elige uno para la mañana, uno para el mediodía y uno para la noche. Esta estructura simple te dará puntos de anclaje a lo largo del día para volver a ti misma.',
        'Recuerda que tu rutina debe servirte a ti, no tú a la rutina. Si un día no sale perfecto, no pasa nada. La flexibilidad es parte del bienestar.',
      ],
    },
    {
      id: 8,
      title: 'Superando Obstáculos Comunes',
      objective: 'Manejar falta de motivación, olvido, cansancio.',
      example: 'Estrategia “1 minuto”.',
      framework: 'Recordatorio visual en lugares clave.',
      content: [
        'Habrá días en los que no tengas ganas. Es normal y humano. En esos días, aplica la regla del minuto.',
        "Dite a ti misma: 'Solo haré esto por un minuto'. A menudo, lo más difícil es empezar. Una vez que estás en movimiento, es más fácil continuar.",
        'Y si te olvidas, sé amable contigo misma. El olvido no es un fracaso, es solo una señal para poner un recordatorio más visible, como una nota en el espejo del baño.',
      ],
    },
    {
      id: 9,
      title: 'Testimonios y Casos Reales',
      objective: 'Inspirar con historias de otras mujeres.',
      example:
        "Carmen, 68 años: 'Nunca pensé que estirar 5 minutos cambiaría tanto mi dolor de espalda'.",
      framework: 'Espacio para reflexión personal.',
      content: [
        "Carmen, de 68 años, nos cuenta: 'Al principio era escéptica. ¿Qué pueden hacer 5 minutos? Pero después de un mes estirando suavemente cada mañana, mi dolor de espalda ha disminuido notablemente'.",
        "Elena, 72 años: 'Lo que más me ha ayudado es la rutina de noche. Dormir mejor me ha cambiado el humor durante el día'.",
        'Estas historias son recordatorios de que nunca es tarde para empezar a cuidarse y que los pequeños cambios suman grandes resultados.',
      ],
    },
    {
      id: 10,
      title: 'Recursos Adicionales',
      objective: 'Proveer herramientas extra para profundizar.',
      example: 'Lista de lecturas recomendadas y música relajante.',
      framework: 'Lista de verificación final.',
      content: [
        'Para acompañar tus rutinas, te recomendamos buscar música clásica suave o sonidos de naturaleza para tus momentos de respiración.',
        'Libros recomendados sobre envejecimiento activo y mindfulness pueden ser excelentes compañeros de viaje.',
        'Gracias por permitirnos acompañarte en este camino hacia tus Rutinas Doradas. ¡Que cada día sea una oportunidad para brillar!',
      ],
    },
  ],
}
